import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/models/Notification.interface';
import { Subject, takeUntil } from 'rxjs';
import { NavigationService } from 'app/core/navigation/navigation.service';

@Component({
  selector: 'notifications',
  templateUrl: './notifications.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'notifications',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    DatePipe,
    TranslocoModule,
  ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _notificationsService = inject(NotificationsService);
  private _overlay = inject(Overlay);
  private _snackBar = inject(MatSnackBar);
  private _translocoService = inject(TranslocoService);
  private _viewContainerRef = inject(ViewContainerRef);
  private _navigationService = inject(NavigationService);

  @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
  @ViewChild('notificationsPanel')
  private _notificationsPanel: TemplateRef<any>;

  notifications: Notification[] = [];
  unreadCount = 0;
  private _overlayRef: OverlayRef;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  /**
   * Constructor
   */
  constructor() {}

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to notification changes
    this._navigationService.updateNavigationBadge('Inbox', 0);
    this._notificationsService.notifications$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((notifications: Notification[]) => {
        // Filter out archived notifications
        this.notifications = notifications.filter(
          notification => !notification.isArchived
        );

        // Calculate the unread count
        this._calculateUnreadCount();

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Update the badge on the navigation
        this._navigationService.updateNavigationBadge(
          'Inbox',
          this.unreadCount
        );
      });

    // Fetch all notifications (by default, get all types)
    this._notificationsService.getNotifications(3).subscribe();
  }

  /**
   * Refresh notifications by refetching them from the server
   */
  refreshNotifications(): void {
    this._notificationsService.getNotifications(3).subscribe(notifications => {
      // Filter out archived notifications
      this.notifications = notifications.filter(
        notification => !notification.isArchived
      );

      // Calculate the unread count
      this._calculateUnreadCount();
      this._changeDetectorRef.markForCheck();

      // Show a MatSnackBar for success
      this._snackBar.open(
        this._translocoService.translate('notifications.refreshSuccess'),
        this._translocoService.translate('common.close'),
        {
          duration: 3000, // Duration in milliseconds
          verticalPosition: 'top', // Position at the top of the page
          horizontalPosition: 'center', // Centered horizontally (optional)
          panelClass: 'app-notification-success',
        }
      );
    });
  }

  /**
   * Open the notifications panel
   */
  openPanel(): void {
    if (!this._notificationsPanel || !this._notificationsOrigin) {
      return;
    }

    if (!this._overlayRef) {
      this._createOverlay();
    }

    this._overlayRef.attach(
      new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
    );
  }

  /**
   * Close the notifications panel
   */
  closePanel(): void {
    this._overlayRef.detach();
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this._notificationsService.markAllAsRead().subscribe(() => {
      this._calculateUnreadCount();
    });
  }

  /**
   * Toggle read status of the given notification
   */
  toggleRead(notification: Notification): void {
    const newStatus = notification.isUnread ? 1 : 2;

    this._notificationsService
      .updateNotificationStatus(notification.id, newStatus)
      .subscribe(() => {
        this._notificationsService
          .getNotifications(3)
          .subscribe(notifications => {
            this.notifications = notifications.filter(
              notification => !notification.isArchived
            );
            this._calculateUnreadCount();
            this._changeDetectorRef.markForCheck();
          });
      });
  }

  /**
   * Delete (Archive) the given notification
   */
  delete(notification: Notification): void {
    this._notificationsService
      .updateNotificationStatus(notification.id, 4)
      .subscribe(() => {
        this._notificationsService
          .getNotifications(3)
          .subscribe(notifications => {
            this.notifications = notifications.filter(
              notification => !notification.isArchived
            );
            this._calculateUnreadCount();
            this._changeDetectorRef.markForCheck();
          });
      });
  }

  /**
   * Track by function for ngFor loops
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  /**
   * Create the overlay
   */
  private _createOverlay(): void {
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(
          this._notificationsOrigin._elementRef.nativeElement
        )
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
        ]),
    });

    this._overlayRef.backdropClick().subscribe(() => {
      this._overlayRef.detach();
    });
  }
  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    // Dispose of the overlay
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }
  /**
   * Calculate the unread count
   */
  private _calculateUnreadCount(): void {
    this.unreadCount = this.notifications.filter(
      notification => notification.isUnread
    ).length;
  }
}
